import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';
import { ArrowRight, Layers, Car, Tag, Tv } from 'lucide-react';

// Portfolio data - updated descriptions
const portfolioItems = [
  {
    id: 'akas',
    name: 'AKAS',
    description: 'Business and brand building platform',
    icon: Layers,
    color: '#6366F1'
  },
  {
    id: 'fairfare',
    name: 'FairFare',
    description: 'Smarter fare comparison and savings',
    icon: Car,
    color: '#10B981'
  },
  {
    id: 'scansavvy',
    name: 'ScanSavvy',
    description: 'Coupon and savings platform',
    icon: Tag,
    color: '#F59E0B'
  },
  {
    id: 'forgetv',
    name: 'ForgeTV',
    description: 'Media and content platform',
    icon: Tv,
    color: '#EF4444'
  }
];

// Hero Section - Refined
const HeroSection = () => {
  const [heroRef, isHeroVisible] = useScrollAnimation();

  return (
    <section 
      ref={heroRef}
      className="min-h-[85vh] flex items-center justify-center px-6 md:px-12"
      data-testid="hero-section"
    >
      <div className="max-w-4xl mx-auto text-center">
        <h1 
          className={`text-5xl md:text-7xl font-semibold tracking-tight text-[#1F2933] leading-[1.05] mb-6 transition-all duration-700 ${
            isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          data-testid="hero-title"
        >
          MJV Holdings
        </h1>
        
        <p 
          className={`text-xl md:text-2xl font-medium text-[#374151] mb-5 transition-all duration-700 delay-100 ${
            isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          data-testid="hero-subtitle"
        >
          Building practical digital companies for everyday people
        </p>
        
        <p 
          className={`text-base md:text-lg text-[#6B7280] max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-700 delay-200 ${
            isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          data-testid="hero-description"
        >
          MJV Holdings is the parent company behind a growing portfolio of products focused on savings, media, and AI-powered tools that solve real-world problems.
        </p>
        
        <div 
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${
            isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to="/portfolio"
            className="bg-[#1F2933] text-white px-8 py-3.5 rounded-lg font-medium hover:bg-[#374151] transition-all duration-200 flex items-center gap-2 group shadow-sm"
            data-testid="hero-cta-portfolio"
          >
            Explore Portfolio
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/about"
            className="text-[#374151] px-8 py-3.5 font-medium hover:text-[#1F2933] transition-colors duration-200 border border-[#E5E7EB] rounded-lg hover:border-[#D1D5DB] hover:bg-white"
            data-testid="hero-cta-about"
          >
            About MJV
          </Link>
        </div>
      </div>
    </section>
  );
};

// Portfolio Preview Section - Stronger, more credible
const PortfolioPreview = () => {
  const [containerRef, visibleItems] = useStaggeredAnimation(portfolioItems.length, 100);
  const [sectionRef, isSectionVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 px-6 md:px-12 bg-white border-t border-[#E5E7EB]"
      data-testid="portfolio-preview-section"
    >
      <div className="max-w-6xl mx-auto">
        <div 
          className={`mb-12 transition-all duration-600 ${
            isSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1F2933] mb-3">
            Our Companies
          </h2>
          <p className="text-[#6B7280] text-base md:text-lg">
            Products built and operated by MJV Holdings
          </p>
        </div>

        <div 
          ref={containerRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {portfolioItems.map((item, index) => {
            const IconComponent = item.icon;
            const isVisible = visibleItems.includes(index);
            
            return (
              <Link
                key={item.id}
                to="/portfolio"
                className={`group bg-[#FAFAFA] hover:bg-white p-6 rounded-xl border border-[#E5E7EB] hover:border-[#D1D5DB] hover:shadow-md transition-all duration-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ 
                  transition: 'opacity 0.5s ease-out, transform 0.5s ease-out, background-color 0.2s, border-color 0.2s, box-shadow 0.2s'
                }}
                data-testid={`portfolio-preview-${item.id}`}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-transform duration-200 group-hover:scale-105"
                  style={{ backgroundColor: `${item.color}12` }}
                >
                  <IconComponent size={20} style={{ color: item.color }} />
                </div>
                
                <h3 className="text-lg font-semibold text-[#1F2933] mb-1.5 group-hover:text-[#374151] transition-colors">
                  {item.name}
                </h3>
                
                <p className="text-sm text-[#6B7280] leading-relaxed">
                  {item.description}
                </p>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-[#1F2933] font-medium hover:text-[#6366F1] transition-colors group text-sm"
            data-testid="view-all-portfolio-link"
          >
            View full portfolio
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// About Teaser Section - More grounded
const AboutTeaser = () => {
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 px-6 md:px-12 border-t border-[#E5E7EB]"
      data-testid="about-teaser-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div 
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1F2933] mb-4">
              About MJV Holdings
            </h2>
            <p className="text-[#4B5563] text-base md:text-lg leading-relaxed mb-6">
              Founded by Joel Williams in Atlanta, Georgia. MJV Holdings builds and operates digital products that help everyday people save time, save money, and get more done.
            </p>
            <p className="text-[#6B7280] leading-relaxed mb-8">
              We focus on practical technology — tools that solve real problems without unnecessary complexity. Each company in our portfolio is built to deliver clear value.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#1F2933] font-medium hover:text-[#6366F1] transition-colors group text-sm"
              data-testid="about-learn-more-link"
            >
              Learn more about MJV
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          
          <div 
            className={`transition-all duration-700 delay-150 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="bg-[#1F2933] rounded-2xl p-8 md:p-10 text-white">
              <div className="space-y-6">
                <div>
                  <p className="text-3xl md:text-4xl font-semibold mb-1">4</p>
                  <p className="text-[#9CA3AF] text-sm">Active Products</p>
                </div>
                <div className="h-px bg-[#374151]" />
                <div>
                  <p className="text-lg font-medium mb-1">Atlanta, GA</p>
                  <p className="text-[#9CA3AF] text-sm">Headquarters</p>
                </div>
                <div className="h-px bg-[#374151]" />
                <div>
                  <p className="text-lg font-medium mb-1">Joel Williams</p>
                  <p className="text-[#9CA3AF] text-sm">Founder</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <Layout>
      <HeroSection />
      <PortfolioPreview />
      <AboutTeaser />
    </Layout>
  );
};

export default HomePage;
